import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type {
  BalanceTopupRequest,
  PaginationMeta,
  TeacherBalanceSummary,
  TeacherBalanceTransaction,
  TopupPaymentMethod,
  TopupRequestStatus,
} from '@/types/tts'

interface ApiEnvelope<T> {
  success?: boolean
  message?: string
  data?: T
  meta?: PaginationMeta
}

export interface CreateTopupRequestInput {
  amount: string
  payment_method: TopupPaymentMethod
  note?: string
  proof?: File | null
}

export const teacherBalanceApi = {
  async getBalance(): Promise<TeacherBalanceSummary> {
    const response = await apiClient.get<ApiEnvelope<TeacherBalanceSummary>>(
      API_ENDPOINTS.TEACHER_BALANCE.SHOW,
    )
    return response.data.data as TeacherBalanceSummary
  },

  async getTransactions(page = 1, perPage = 20): Promise<{ data: TeacherBalanceTransaction[]; meta?: PaginationMeta }> {
    const response = await apiClient.get<ApiEnvelope<TeacherBalanceTransaction[]>>(
      API_ENDPOINTS.TEACHER_BALANCE.TRANSACTIONS,
      { params: { page, per_page: perPage } },
    )
    return { data: response.data.data ?? [], meta: response.data.meta }
  },

  async listTopupRequests(page = 1, perPage = 20): Promise<{ data: BalanceTopupRequest[]; meta?: PaginationMeta }> {
    const response = await apiClient.get<ApiEnvelope<BalanceTopupRequest[]>>(
      API_ENDPOINTS.TEACHER_BALANCE.TOPUP_REQUESTS,
      { params: { page, per_page: perPage } },
    )
    return { data: response.data.data ?? [], meta: response.data.meta }
  },

  async createTopupRequest(input: CreateTopupRequestInput): Promise<BalanceTopupRequest> {
    const formData = new FormData()
    formData.append('amount', input.amount)
    formData.append('payment_method', input.payment_method)
    if (input.note) formData.append('note', input.note)
    if (input.proof) formData.append('proof', input.proof)

    const response = await apiClient.post<ApiEnvelope<BalanceTopupRequest>>(
      API_ENDPOINTS.TEACHER_BALANCE.TOPUP_REQUESTS,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data.data as BalanceTopupRequest
  },

  async cancelTopupRequest(id: number): Promise<BalanceTopupRequest> {
    const response = await apiClient.post<ApiEnvelope<BalanceTopupRequest>>(
      API_ENDPOINTS.TEACHER_BALANCE.TOPUP_CANCEL(id),
    )
    return response.data.data as BalanceTopupRequest
  },

  // Админ
  async adminListTopupRequests(
    status?: TopupRequestStatus,
    page = 1,
    perPage = 20,
  ): Promise<{ data: BalanceTopupRequest[]; meta?: PaginationMeta }> {
    const response = await apiClient.get<ApiEnvelope<BalanceTopupRequest[]>>(
      API_ENDPOINTS.ADMIN.BALANCE_TOPUP_REQUESTS,
      { params: { status, page, per_page: perPage } },
    )
    return { data: response.data.data ?? [], meta: response.data.meta }
  },

  async adminConfirmTopupRequest(id: number, decisionNote?: string): Promise<BalanceTopupRequest> {
    const response = await apiClient.post<ApiEnvelope<BalanceTopupRequest>>(
      API_ENDPOINTS.ADMIN.BALANCE_TOPUP_CONFIRM(id),
      { decision_note: decisionNote },
    )
    return response.data.data as BalanceTopupRequest
  },

  async adminRejectTopupRequest(id: number, decisionNote?: string): Promise<BalanceTopupRequest> {
    const response = await apiClient.post<ApiEnvelope<BalanceTopupRequest>>(
      API_ENDPOINTS.ADMIN.BALANCE_TOPUP_REJECT(id),
      { decision_note: decisionNote },
    )
    return response.data.data as BalanceTopupRequest
  },

  async adminAdjustBalance(
    userId: number,
    amount: string,
    direction: 'credit' | 'debit',
    description: string,
  ): Promise<{ balance: string }> {
    const response = await apiClient.post<ApiEnvelope<{ balance: string }>>(
      API_ENDPOINTS.ADMIN.USER_BALANCE_ADJUST(userId),
      { amount, direction, description },
    )
    return response.data.data as { balance: string }
  },
}
